import type { Lang } from '@/i18n/locale';

/** Only explicitly approved Upper16, plan-only and straight-bar pressdown labels. */
export const APPROVED_FITNESS_LABEL_IDS = [
  "incline-barbell-bench-press",
  "decline-barbell-bench-press",
  "incline-dumbbell-bench-press",
  "incline-dumbbell-fly",
  "one-arm-supported-dumbbell-row",
  "standing-barbell-curl",
  "standing-alternating-dumbbell-curl",
  "standing-dumbbell-hammer-curl",
  "standing-barbell-reverse-curl",
  "seated-incline-dumbbell-curl",
  "flat-barbell-skull-crusher",
  "supported-one-arm-dumbbell-kickback",
  "standing-one-arm-overhead-dumbbell-extension",
  "plan-front-squat",
  "plan-pause-squat",
  "plan-deficit-deadlift",
  "plan-box-jump",
  "battle-ropes",
  "standing-chest-height-cable-fly",
  "standing-bilateral-cable-row",
  "bent-over-bilateral-dumbbell-row",
  "standing-straight-bar-triceps-pressdown",
  "free-triceps-frances"
] as const;
export type ApprovedFitnessLabelId = typeof APPROVED_FITNESS_LABEL_IDS[number];
export const APPROVED_FITNESS_EXERCISE_LABELS = {
  "EN": {
    "incline-barbell-bench-press": "Incline Barbell Bench Press",
    "decline-barbell-bench-press": "Decline Barbell Bench Press",
    "incline-dumbbell-bench-press": "Incline Dumbbell Bench Press",
    "incline-dumbbell-fly": "Incline Dumbbell Fly",
    "one-arm-supported-dumbbell-row": "One-arm Dumbbell Row with Bench Support",
    "standing-barbell-curl": "Standing Straight-bar Curl",
    "standing-alternating-dumbbell-curl": "Standing Alternating Dumbbell Curl",
    "standing-dumbbell-hammer-curl": "Standing Bilateral Hammer Curl",
    "standing-barbell-reverse-curl": "Standing Reverse Straight-bar Curl",
    "seated-incline-dumbbell-curl": "Seated Incline Dumbbell Curl",
    "flat-barbell-skull-crusher": "Lying Straight-bar Triceps Extension",
    "supported-one-arm-dumbbell-kickback": "One-arm Dumbbell Kickback with Bench Support",
    "standing-one-arm-overhead-dumbbell-extension": "Standing One-arm Overhead Dumbbell Extension",
    "plan-front-squat": "Barbell Front Squat",
    "plan-pause-squat": "Paused High-bar Back Squat",
    "plan-deficit-deadlift": "Conventional Barbell Deficit Deadlift",
    "plan-box-jump": "Box Jump with Step-down",
    "battle-ropes": "Battle Ropes (Alternating Waves)",
    "standing-chest-height-cable-fly": "Standing Chest-height Cable Fly",
    "standing-bilateral-cable-row": "Standing Bilateral Cable Row",
    "bent-over-bilateral-dumbbell-row": "Bent-over Bilateral Dumbbell Row",
    "standing-straight-bar-triceps-pressdown": "Standing Straight-bar Triceps Pressdown",
    "free-triceps-frances": "Seated Two-Hand Dumbbell Overhead Extension"
  },
  "PT-BR": {
    "incline-barbell-bench-press": "Supino inclinado com barra",
    "decline-barbell-bench-press": "Supino declinado com barra",
    "incline-dumbbell-bench-press": "Supino inclinado com halteres",
    "incline-dumbbell-fly": "Crucifixo inclinado com halteres",
    "one-arm-supported-dumbbell-row": "Remada com um halter e apoio no banco",
    "standing-barbell-curl": "Rosca em pé com barra reta",
    "standing-alternating-dumbbell-curl": "Rosca alternada com halteres em pé",
    "standing-dumbbell-hammer-curl": "Rosca martelo bilateral em pé",
    "standing-barbell-reverse-curl": "Rosca inversa com barra reta em pé",
    "seated-incline-dumbbell-curl": "Rosca com halteres no banco inclinado",
    "flat-barbell-skull-crusher": "Tríceps testa com barra reta no banco plano",
    "supported-one-arm-dumbbell-kickback": "Tríceps coice unilateral com apoio no banco",
    "standing-one-arm-overhead-dumbbell-extension": "Extensão de tríceps unilateral acima da cabeça em pé",
    "plan-front-squat": "Agachamento frontal com barra",
    "plan-pause-squat": "Agachamento com barra nas costas e pausa",
    "plan-deficit-deadlift": "Levantamento terra convencional com barra em déficit",
    "plan-box-jump": "Salto na caixa com descida em passos",
    "battle-ropes": "Cordas navais (ondas alternadas)",
    "standing-chest-height-cable-fly": "Crucifixo em pé no cabo à altura do peito",
    "standing-bilateral-cable-row": "Remada bilateral em pé no cabo",
    "bent-over-bilateral-dumbbell-row": "Remada curvada bilateral com halteres",
    "standing-straight-bar-triceps-pressdown": "Tríceps em pé na polia alta com barra reta",
    "free-triceps-frances": "Tríceps acima da cabeça sentado com um halter e duas mãos"
  },
  "NL": {
    "incline-barbell-bench-press": "Schuin bankdrukken met halterstang",
    "decline-barbell-bench-press": "Bankdrukken met halterstang op aflopende bank",
    "incline-dumbbell-bench-press": "Schuin bankdrukken met dumbbells",
    "incline-dumbbell-fly": "Dumbbell-fly op schuine bank",
    "one-arm-supported-dumbbell-row": "Eenarmig dumbbell-roeien met banksteun",
    "standing-barbell-curl": "Staande curl met rechte halterstang",
    "standing-alternating-dumbbell-curl": "Staande afwisselende dumbbell-curl",
    "standing-dumbbell-hammer-curl": "Staande tweearmige hammer-curl",
    "standing-barbell-reverse-curl": "Staande omgekeerde curl met rechte halterstang",
    "seated-incline-dumbbell-curl": "Zittende dumbbell-curl tegen schuine rugleuning",
    "flat-barbell-skull-crusher": "Liggende tricepsextensie met rechte halterstang",
    "supported-one-arm-dumbbell-kickback": "Eenarmige dumbbell-kickback met banksteun",
    "standing-one-arm-overhead-dumbbell-extension": "Staande eenarmige tricepsextensie boven het hoofd",
    "plan-front-squat": "Front squat met halterstang",
    "plan-pause-squat": "High-bar back squat met pauze",
    "plan-deficit-deadlift": "Conventionele deficit-deadlift met halterstang",
    "plan-box-jump": "Box jump met afstappen",
    "battle-ropes": "Battle ropes (afwisselende golven)",
    "standing-chest-height-cable-fly": "Staande cable-fly op borsthoogte",
    "standing-bilateral-cable-row": "Staand tweearmig roeien aan kabel",
    "bent-over-bilateral-dumbbell-row": "Voorovergebogen tweearmig dumbbell-roeien",
    "standing-straight-bar-triceps-pressdown": "Staande triceps-pressdown met rechte stang",
    "free-triceps-frances": "Zittende tricepsextensie boven het hoofd met één dumbbell en twee handen"
  },
  "DE": {
    "incline-barbell-bench-press": "Schrägbankdrücken mit Langhantel",
    "decline-barbell-bench-press": "Negativbankdrücken mit Langhantel",
    "incline-dumbbell-bench-press": "Schrägbankdrücken mit Kurzhanteln",
    "incline-dumbbell-fly": "Fliegende mit Kurzhanteln auf der Schrägbank",
    "one-arm-supported-dumbbell-row": "Einarmiges Kurzhantelrudern mit Bankstütze",
    "standing-barbell-curl": "Curl mit gerader Langhantel im Stehen",
    "standing-alternating-dumbbell-curl": "Abwechselnder Kurzhantelcurl im Stehen",
    "standing-dumbbell-hammer-curl": "Beidarmiger Hammercurl im Stehen",
    "standing-barbell-reverse-curl": "Reverse-Curl mit gerader Langhantel im Stehen",
    "seated-incline-dumbbell-curl": "Kurzhantelcurl im Sitzen auf der Schrägbank",
    "flat-barbell-skull-crusher": "Liegendes Trizepsstrecken mit gerader Langhantel",
    "supported-one-arm-dumbbell-kickback": "Einarmiger Kurzhantel-Kickback mit Bankstütze",
    "standing-one-arm-overhead-dumbbell-extension": "Einarmiges Kurzhantel-Trizepsstrecken über Kopf im Stehen",
    "plan-front-squat": "Frontkniebeuge mit Langhantel",
    "plan-pause-squat": "High-Bar-Kniebeuge mit Pause",
    "plan-deficit-deadlift": "Konventionelles Langhantel-Kreuzheben vom Defizit",
    "plan-box-jump": "Boxsprung mit Heruntersteigen",
    "battle-ropes": "Battle Ropes (abwechselnde Wellen)",
    "standing-chest-height-cable-fly": "Kabel-Fly im Stehen auf Brusthöhe",
    "standing-bilateral-cable-row": "Beidarmiges Kabelrudern im Stehen",
    "bent-over-bilateral-dumbbell-row": "Vorgebeugtes beidarmiges Kurzhantelrudern",
    "standing-straight-bar-triceps-pressdown": "Trizepsdrücken am hohen Kabelzug mit gerader Stange",
    "free-triceps-frances": "Trizepsstrecken über Kopf im Sitzen mit einer Kurzhantel und beiden Händen"
  },
  "IT": {
    "incline-barbell-bench-press": "Panca inclinata con bilanciere",
    "decline-barbell-bench-press": "Panca declinata con bilanciere",
    "incline-dumbbell-bench-press": "Panca inclinata con manubri",
    "incline-dumbbell-fly": "Croci con manubri su panca inclinata",
    "one-arm-supported-dumbbell-row": "Rematore a un braccio con appoggio sulla panca",
    "standing-barbell-curl": "Curl in piedi con bilanciere dritto",
    "standing-alternating-dumbbell-curl": "Curl alternato con manubri in piedi",
    "standing-dumbbell-hammer-curl": "Curl a martello bilaterale in piedi",
    "standing-barbell-reverse-curl": "Curl inverso con bilanciere dritto in piedi",
    "seated-incline-dumbbell-curl": "Curl con manubri su panca inclinata",
    "flat-barbell-skull-crusher": "Estensioni tricipiti con bilanciere dritto su panca piana",
    "supported-one-arm-dumbbell-kickback": "Kickback a un braccio con appoggio sulla panca",
    "standing-one-arm-overhead-dumbbell-extension": "Estensione tricipiti a un braccio sopra la testa in piedi",
    "plan-front-squat": "Squat frontale con bilanciere",
    "plan-pause-squat": "Back squat con bilanciere alto e pausa",
    "plan-deficit-deadlift": "Stacco convenzionale con bilanciere in deficit",
    "plan-box-jump": "Salto sul box con discesa a passi",
    "battle-ropes": "Corde da battaglia (onde alternate)",
    "standing-chest-height-cable-fly": "Croci ai cavi in piedi all'altezza del petto",
    "standing-bilateral-cable-row": "Rematore bilaterale ai cavi in piedi",
    "bent-over-bilateral-dumbbell-row": "Rematore bilaterale con manubri a busto inclinato",
    "standing-straight-bar-triceps-pressdown": "Pushdown tricipiti in piedi con barra dritta",
    "free-triceps-frances": "Estensione dei tricipiti sopra la testa da seduti con un manubrio e due mani"
  },
  "FR": {
    "incline-barbell-bench-press": "Développé incliné à la barre",
    "decline-barbell-bench-press": "Développé décliné à la barre",
    "incline-dumbbell-bench-press": "Développé incliné aux haltères",
    "incline-dumbbell-fly": "Écarté incliné aux haltères",
    "one-arm-supported-dumbbell-row": "Rowing à un bras avec appui sur banc",
    "standing-barbell-curl": "Curl debout à la barre droite",
    "standing-alternating-dumbbell-curl": "Curl alterné debout aux haltères",
    "standing-dumbbell-hammer-curl": "Curl marteau bilatéral debout",
    "standing-barbell-reverse-curl": "Curl inversé debout à la barre droite",
    "seated-incline-dumbbell-curl": "Curl assis aux haltères sur banc incliné",
    "flat-barbell-skull-crusher": "Extension triceps couché à la barre droite",
    "supported-one-arm-dumbbell-kickback": "Kickback à un bras avec appui sur banc",
    "standing-one-arm-overhead-dumbbell-extension": "Extension triceps debout à un bras au-dessus de la tête",
    "plan-front-squat": "Squat avant à la barre",
    "plan-pause-squat": "Squat arrière barre haute avec pause",
    "plan-deficit-deadlift": "Soulevé de terre conventionnel à la barre en déficit",
    "plan-box-jump": "Saut sur caisson avec descente en marchant",
    "battle-ropes": "Cordes ondulatoires (vagues alternées)",
    "standing-chest-height-cable-fly": "Écarté aux câbles debout à hauteur de poitrine",
    "standing-bilateral-cable-row": "Tirage bilatéral debout à la poulie",
    "bent-over-bilateral-dumbbell-row": "Rowing bilatéral aux haltères buste penché",
    "standing-straight-bar-triceps-pressdown": "Extension triceps debout à la poulie haute avec barre droite",
    "free-triceps-frances": "Extension des triceps au-dessus de la tête, assis, avec un haltère à deux mains"
  },
  "PL": {
    "incline-barbell-bench-press": "Wyciskanie sztangi na ławce skośnej",
    "decline-barbell-bench-press": "Wyciskanie sztangi na ławce ze skosem ujemnym",
    "incline-dumbbell-bench-press": "Wyciskanie hantli na ławce skośnej",
    "incline-dumbbell-fly": "Rozpiętki z hantlami na ławce skośnej",
    "one-arm-supported-dumbbell-row": "Wiosłowanie hantlem z podparciem na ławce",
    "standing-barbell-curl": "Uginanie ramion ze sztangą prostą stojąc",
    "standing-alternating-dumbbell-curl": "Naprzemienne uginanie z hantlami stojąc",
    "standing-dumbbell-hammer-curl": "Oburęczne uginanie młotkowe stojąc",
    "standing-barbell-reverse-curl": "Odwrotne uginanie ze sztangą prostą stojąc",
    "seated-incline-dumbbell-curl": "Uginanie z hantlami na ławce skośnej",
    "flat-barbell-skull-crusher": "Prostowanie tricepsów ze sztangą prostą leżąc",
    "supported-one-arm-dumbbell-kickback": "Kickback hantlem z podparciem na ławce",
    "standing-one-arm-overhead-dumbbell-extension": "Prostowanie tricepsa jednorącz nad głową stojąc",
    "plan-front-squat": "Przysiad przedni ze sztangą",
    "plan-pause-squat": "Przysiad ze sztangą wysoko na plecach z pauzą",
    "plan-deficit-deadlift": "Klasyczny martwy ciąg ze sztangą z podwyższenia",
    "plan-box-jump": "Wskok na skrzynię z zejściem krokami",
    "battle-ropes": "Liny treningowe (fale naprzemienne)",
    "standing-chest-height-cable-fly": "Rozpiętki na wyciągu stojąc na wysokości klatki",
    "standing-bilateral-cable-row": "Oburęczne wiosłowanie stojąc na wyciągu",
    "bent-over-bilateral-dumbbell-row": "Oburęczne wiosłowanie hantlami w opadzie",
    "standing-straight-bar-triceps-pressdown": "Prostowanie tricepsów stojąc na wyciągu górnym z prostym drążkiem",
    "free-triceps-frances": "Prostowanie ramion nad głową w siadzie z jednym hantlem trzymanym oburącz"
  },
  "BG": {
    "incline-barbell-bench-press": "Изтласкване на щанга от наклонена пейка",
    "decline-barbell-bench-press": "Изтласкване на щанга от пейка с обратен наклон",
    "incline-dumbbell-bench-press": "Изтласкване на дъмбели от наклонена пейка",
    "incline-dumbbell-fly": "Флайс с дъмбели на наклонена пейка",
    "one-arm-supported-dumbbell-row": "Гребане с един дъмбел с опора на пейка",
    "standing-barbell-curl": "Сгъване с права щанга от стоеж",
    "standing-alternating-dumbbell-curl": "Редуващо сгъване с дъмбели от стоеж",
    "standing-dumbbell-hammer-curl": "Двустранно чуково сгъване от стоеж",
    "standing-barbell-reverse-curl": "Обратно сгъване с права щанга от стоеж",
    "seated-incline-dumbbell-curl": "Сгъване с дъмбели седнал на наклонена пейка",
    "flat-barbell-skull-crusher": "Разгъване за трицепс с права щанга от лег",
    "supported-one-arm-dumbbell-kickback": "Кикбек с един дъмбел с опора на пейка",
    "standing-one-arm-overhead-dumbbell-extension": "Разгъване за трицепс с една ръка над глава от стоеж",
    "plan-front-squat": "Преден клек с щанга",
    "plan-pause-squat": "Клек с щанга високо на гърба и пауза",
    "plan-deficit-deadlift": "Класическа тяга с щанга от дефицит",
    "plan-box-jump": "Скок върху кутия със слизане на стъпки",
    "battle-ropes": "Бойни въжета (редуващи се вълни)",
    "standing-chest-height-cable-fly": "Флайс от стоеж на скрипец на височината на гърдите",
    "standing-bilateral-cable-row": "Двустранно гребане от стоеж на скрипец",
    "bent-over-bilateral-dumbbell-row": "Двустранно гребане с дъмбели от наклон",
    "standing-straight-bar-triceps-pressdown": "Разгъване за трицепс от стоеж на горен скрипец с права ръкохватка",
    "free-triceps-frances": "Разгъване за трицепс над глава от седеж с един дъмбел, държан с две ръце"
  },
  "ES": {
    "incline-barbell-bench-press": "Press inclinado con barra",
    "decline-barbell-bench-press": "Press declinado con barra",
    "incline-dumbbell-bench-press": "Press inclinado con mancuernas",
    "incline-dumbbell-fly": "Aperturas inclinadas con mancuernas",
    "one-arm-supported-dumbbell-row": "Remo con una mancuerna y apoyo en banco",
    "standing-barbell-curl": "Curl de pie con barra recta",
    "standing-alternating-dumbbell-curl": "Curl alterno de pie con mancuernas",
    "standing-dumbbell-hammer-curl": "Curl martillo bilateral de pie",
    "standing-barbell-reverse-curl": "Curl inverso de pie con barra recta",
    "seated-incline-dumbbell-curl": "Curl sentado con mancuernas en banco inclinado",
    "flat-barbell-skull-crusher": "Extensión de tríceps tumbado con barra recta",
    "supported-one-arm-dumbbell-kickback": "Patada de tríceps con apoyo en banco",
    "standing-one-arm-overhead-dumbbell-extension": "Extensión de tríceps de pie a un brazo sobre la cabeza",
    "plan-front-squat": "Sentadilla frontal con barra",
    "plan-pause-squat": "Sentadilla trasera con barra alta y pausa",
    "plan-deficit-deadlift": "Peso muerto convencional con barra en déficit",
    "plan-box-jump": "Salto al cajón con bajada por pasos",
    "battle-ropes": "Cuerdas de batalla (ondas alternas)",
    "standing-chest-height-cable-fly": "Aperturas de pie en poleas a la altura del pecho",
    "standing-bilateral-cable-row": "Remo bilateral de pie en polea",
    "bent-over-bilateral-dumbbell-row": "Remo bilateral inclinado con mancuernas",
    "standing-straight-bar-triceps-pressdown": "Extensión de tríceps de pie en polea alta con barra recta",
    "free-triceps-frances": "Extensión de tríceps por encima de la cabeza sentado con una mancuerna y ambas manos"
  }
} satisfies Record<Lang, Record<ApprovedFitnessLabelId, string>>;
